import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { todosAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Plus, 
  CheckCircle2, 
  Circle,
  Trash2,
  Calendar,
  Filter,
  ListTodo,
  Globe,
  Share2,
  MoreHorizontal,
  User,
  Users,
  Edit2,
  Eye,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const CATEGORY_ICONS = {
  "Website": Globe,
  "Social Media": Share2,
  "Miscellaneous": MoreHorizontal,
};

const PRIORITY_COLORS = {
  "High": { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500", border: "border-l-red-500" },
  "Medium": { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500", border: "border-l-orange-500" },
  "Low": { bg: "bg-yellow-50", text: "text-yellow-700", dot: "bg-yellow-500", border: "border-l-yellow-500" },
};

const TodoList = () => {
  const { user, isFounder } = useAuth();
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  
  const isFounderUser = isFounder();
  
  const [newTodo, setNewTodo] = useState({
    title: '',
    category: 'Miscellaneous',
    priority: 'Medium',
    due_date: '',
    notes: '',
    assigned_to: '',
  });

  const [editTodo, setEditTodo] = useState({
    id: '',
    title: '',
    category: 'Miscellaneous',
    priority: 'Medium',
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [filterUser]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterUser !== 'all' && isFounderUser) {
        params.owner_id = filterUser;
      }
      
      const [todosRes, categoriesRes, usersRes] = await Promise.all([
        todosAPI.getAll(params),
        todosAPI.getCategories(),
        todosAPI.getUsers(),
      ]);
      setTodos(todosRes.data);
      setCategories(categoriesRes.data.categories);
      setPriorities(categoriesRes.data.priorities);
      setUsers(usersRes.data.users);
    } catch (error) {
      toast.error('Failed to fetch todos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTodo = async () => {
    if (!newTodo.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      const todoData = { ...newTodo };
      // If founder and no assignee selected, assign to self
      if (isFounderUser && !todoData.assigned_to) {
        todoData.assigned_to = user.id;
      }
      
      await todosAPI.create(todoData);
      toast.success('Task added!');
      setShowAddSheet(false);
      setNewTodo({
        title: '',
        category: 'Miscellaneous',
        priority: 'Medium',
        due_date: '',
        notes: '',
        assigned_to: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleTodo = async (id) => {
    try {
      await todosAPI.toggle(id);
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTodo = async (id) => {
    try {
      await todosAPI.delete(id);
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleViewTodo = (todo) => {
    setSelectedTodo(todo);
    setShowViewDialog(true);
  };

  const handleOpenEdit = (todo) => {
    setEditTodo({
      id: todo.id,
      title: todo.title,
      category: todo.category,
      priority: todo.priority,
      due_date: todo.due_date || '',
      notes: todo.notes || '',
    });
    setShowEditSheet(true);
  };

  const handleUpdateTodo = async () => {
    if (!editTodo.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      await todosAPI.update(editTodo.id, {
        title: editTodo.title,
        category: editTodo.category,
        priority: editTodo.priority,
        due_date: editTodo.due_date || null,
        notes: editTodo.notes,
      });
      toast.success('Task updated!');
      setShowEditSheet(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getFilteredTodos = () => {
    let filtered = todos;
    
    // Filter by completion status
    if (activeTab === 'pending') {
      filtered = filtered.filter(t => !t.completed);
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(t => t.completed);
    }
    
    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(t => t.category === filterCategory);
    }
    
    return filtered;
  };

  const pendingCount = todos.filter(t => !t.completed).length;
  const completedCount = todos.filter(t => t.completed).length;

  // Group todos by owner for founder view
  const getTodosByOwner = () => {
    const grouped = {};
    todos.forEach(todo => {
      const ownerName = todo.owner_name || 'Unknown';
      if (!grouped[ownerName]) {
        grouped[ownerName] = [];
      }
      grouped[ownerName].push(todo);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="todo-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-zinc-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="todo-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {isFounderUser ? 'Team To-Do List' : 'My To-Do List'}
          </h1>
          <p className="text-zinc-500 mt-1">
            {isFounderUser ? 'Manage tasks for your team' : 'Your personal task manager'}
          </p>
        </div>
        
        <Button
          onClick={() => setShowAddSheet(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          data-testid="add-todo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-100 p-4 text-center">
          <p className="text-3xl font-medium text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            {todos.length}
          </p>
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Total</p>
        </div>
        <div className="bg-white border border-zinc-100 p-4 text-center">
          <p className="text-3xl font-medium text-amber-600" style={{ fontFamily: 'Playfair Display, serif' }}>
            {pendingCount}
          </p>
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Pending</p>
        </div>
        <div className="bg-white border border-zinc-100 p-4 text-center">
          <p className="text-3xl font-medium text-emerald-600" style={{ fontFamily: 'Playfair Display, serif' }}>
            {completedCount}
          </p>
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Completed</p>
        </div>
      </div>

      {/* Filters & Tabs */}
      <div className="bg-white border border-zinc-100">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-zinc-100 px-4">
            <TabsList className="bg-transparent h-auto p-0">
              <TabsTrigger 
                value="all" 
                className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              >
                All ({todos.length})
              </TabsTrigger>
              <TabsTrigger 
                value="pending" 
                className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              >
                Pending ({pendingCount})
              </TabsTrigger>
              <TabsTrigger 
                value="completed" 
                className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              >
                Completed ({completedCount})
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2 py-2 md:py-0">
              {/* User filter - only for founder */}
              {isFounderUser && users.length > 1 && (
                <Select value={filterUser} onValueChange={setFilterUser}>
                  <SelectTrigger className="w-40 border-0" data-testid="todo-user-filter">
                    <Users className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40 border-0" data-testid="todo-category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value={activeTab} className="p-0 mt-0">
            {getFilteredTodos().length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {getFilteredTodos().map((todo) => {
                  const CategoryIcon = CATEGORY_ICONS[todo.category] || MoreHorizontal;
                  const priorityStyle = PRIORITY_COLORS[todo.priority] || PRIORITY_COLORS["Medium"];
                  
                  return (
                    <div 
                      key={todo.id}
                      className={`flex items-center gap-4 p-4 hover:bg-zinc-50 transition-colors border-l-4 ${priorityStyle.border} ${todo.completed ? 'opacity-60' : ''}`}
                      data-testid={`todo-item-${todo.id}`}
                    >
                      <button
                        onClick={() => handleToggleTodo(todo.id)}
                        className="flex-shrink-0"
                        data-testid={`toggle-todo-${todo.id}`}
                      >
                        {todo.completed ? (
                          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-zinc-300 hover:text-zinc-500" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${todo.completed ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
                          {todo.title}
                        </p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-zinc-400">
                            <CategoryIcon className="w-3 h-3" />
                            {todo.category}
                          </span>
                          {todo.due_date && (
                            <span className="flex items-center gap-1 text-xs text-zinc-400">
                              <Calendar className="w-3 h-3" />
                              {todo.due_date}
                            </span>
                          )}
                          {/* Show owner for founder */}
                          {isFounderUser && todo.owner_name && (
                            <span className="flex items-center gap-1 text-xs text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded">
                              <User className="w-3 h-3" />
                              {todo.owner_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <span className={`text-xs px-2 py-1 rounded-md ${priorityStyle.bg} ${priorityStyle.text}`}>
                        {todo.priority}
                      </span>
                      
                      {/* View Details - for all users */}
                      <button
                        onClick={() => handleViewTodo(todo)}
                        className="text-zinc-300 hover:text-zinc-600 transition-colors"
                        data-testid={`view-todo-${todo.id}`}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      {/* Edit - only for founder */}
                      {isFounderUser && (
                        <button
                          onClick={() => handleOpenEdit(todo)}
                          className="text-zinc-300 hover:text-blue-500 transition-colors"
                          data-testid={`edit-todo-${todo.id}`}
                          title="Edit Task"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Delete - only for founder */}
                      {isFounderUser && (
                        <button
                          onClick={() => handleDeleteTodo(todo.id)}
                          className="text-zinc-300 hover:text-red-500 transition-colors"
                          data-testid={`delete-todo-${todo.id}`}
                          title="Delete Task"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <ListTodo className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  No tasks found
                </h3>
                <p className="text-zinc-500 mb-6">
                  {activeTab === 'completed' ? 'No completed tasks yet' : 'Add your first task to get started'}
                </p>
                {activeTab !== 'completed' && (
                  <Button
                    onClick={() => setShowAddSheet(true)}
                    className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
                  >
                    Add Task
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Sheet */}
      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="add-todo-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>Add Task</SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">Create a new to-do item</p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Task <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newTodo.title}
                onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="What needs to be done?"
                data-testid="add-todo-title-input"
              />
            </div>

            {/* Assign to user - only for founder */}
            {isFounderUser && users.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Assign To
                </label>
                <Select
                  value={newTodo.assigned_to || user?.id}
                  onValueChange={(value) => setNewTodo({ ...newTodo, assigned_to: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-todo-assignee-select">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {u.name}
                          {u.id === user?.id && " (Me)"}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Category
                </label>
                <Select
                  value={newTodo.category}
                  onValueChange={(value) => setNewTodo({ ...newTodo, category: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-todo-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Priority
                </label>
                <Select
                  value={newTodo.priority}
                  onValueChange={(value) => setNewTodo({ ...newTodo, priority: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-todo-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((pri) => (
                      <SelectItem key={pri} value={pri}>{pri}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={newTodo.due_date}
                onChange={(e) => setNewTodo({ ...newTodo, due_date: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                data-testid="add-todo-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Notes
              </label>
              <textarea
                value={newTodo.notes}
                onChange={(e) => setNewTodo({ ...newTodo, notes: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all min-h-[80px] resize-none"
                placeholder="Any additional notes..."
                data-testid="add-todo-notes-input"
              />
            </div>

            <Button
              onClick={handleCreateTodo}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="save-todo-btn"
            >
              Add Task
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Sheet - Founder Only */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="edit-todo-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>Edit Task</SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">Update task details</p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Task <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editTodo.title}
                onChange={(e) => setEditTodo({ ...editTodo, title: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="What needs to be done?"
                data-testid="edit-todo-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Category
                </label>
                <Select
                  value={editTodo.category}
                  onValueChange={(value) => setEditTodo({ ...editTodo, category: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="edit-todo-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Priority
                </label>
                <Select
                  value={editTodo.priority}
                  onValueChange={(value) => setEditTodo({ ...editTodo, priority: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="edit-todo-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((pri) => (
                      <SelectItem key={pri} value={pri}>{pri}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={editTodo.due_date}
                onChange={(e) => setEditTodo({ ...editTodo, due_date: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                data-testid="edit-todo-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Notes
              </label>
              <textarea
                value={editTodo.notes}
                onChange={(e) => setEditTodo({ ...editTodo, notes: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all min-h-[80px] resize-none"
                placeholder="Any additional notes..."
                data-testid="edit-todo-notes-input"
              />
            </div>

            <Button
              onClick={handleUpdateTodo}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="update-todo-btn"
            >
              Update Task
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Details Dialog - All Users */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-md" data-testid="view-todo-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Task Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedTodo && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400">Task</label>
                <p className="text-lg font-medium text-zinc-900 mt-1">{selectedTodo.title}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Category</label>
                  <p className="text-sm text-zinc-700 mt-1">{selectedTodo.category}</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Priority</label>
                  <span className={`inline-block text-xs px-2 py-1 rounded-md mt-1 ${PRIORITY_COLORS[selectedTodo.priority]?.bg} ${PRIORITY_COLORS[selectedTodo.priority]?.text}`}>
                    {selectedTodo.priority}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Due Date</label>
                  <p className="text-sm text-zinc-700 mt-1">{selectedTodo.due_date || 'Not set'}</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Status</label>
                  <p className={`text-sm mt-1 ${selectedTodo.completed ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {selectedTodo.completed ? 'Completed' : 'Pending'}
                  </p>
                </div>
              </div>
              
              {selectedTodo.owner_name && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Assigned To</label>
                  <p className="text-sm text-zinc-700 mt-1">{selectedTodo.owner_name}</p>
                </div>
              )}
              
              {selectedTodo.notes && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">Notes</label>
                  <p className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{selectedTodo.notes}</p>
                </div>
              )}
              
              <Button
                onClick={() => setShowViewDialog(false)}
                variant="outline"
                className="w-full mt-4"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TodoList;
